import { Box, styled } from '@mui/material';
import { PolicyEvaluationContext, TimeWindow } from '@provision/sidekick-utils';
import React, { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { PolicyCard } from './policy-card';
import { PolicyCategoryGroupSection } from './policy-category-group';
import { PolicyCategoryGroup } from './policy-groups';

interface SupportPolicyListProps {
  partnerId: number;
  isLoading: boolean;
  policyGroups: PolicyCategoryGroup[];
  expanded: string[];
  handleExpandClick: (id: string) => void;
  hasAccessToManageSupportPolicies: boolean;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  policyEvaluationContext: PolicyEvaluationContext;
  evaluationServiceLevel: string;
  isCategoryOpen: (category: string) => boolean;
  onToggleCategory: (category: string) => void;
  isPreLits?: boolean;
  getEscalationCutoffHoursForUrgency?: (urgencyLevel: string) => TimeWindow[];
  focusPolicyId?: string | null;
  onFocusPolicyHandled?: () => void;
}

const LoadingPlaceholder = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(1.5),
  backgroundColor: theme.palette.action.hover,
  height: 60,
}));

export const SupportPolicyList = ({
  partnerId = PLATFORM_PARTNER_ID,
  isLoading,
  policyGroups,
  expanded,
  handleExpandClick,
  hasAccessToManageSupportPolicies,
  handleMenuOpen,
  policyEvaluationContext,
  evaluationServiceLevel,
  isCategoryOpen,
  onToggleCategory,
  isPreLits,
  getEscalationCutoffHoursForUrgency,
  focusPolicyId,
  onFocusPolicyHandled,
}: SupportPolicyListProps): React.ReactElement => {
  const stableCurrentTime = useMemo(
    () => policyEvaluationContext.currentTime ?? new Date(),
    [policyEvaluationContext.currentTime],
  );

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <>
        <LoadingPlaceholder />
        <LoadingPlaceholder />
        <LoadingPlaceholder />
      </>
    );
  } else if (policyGroups.length === 0) {
    content = (
      <Box
        data-testid="support-policy-list-empty"
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          fontSize: '0.9rem',
        }}
      >
        No policies found
      </Box>
    );
  } else {
    content = policyGroups.map((group, index) => (
      <PolicyCategoryGroupSection
        key={group.category}
        category={group.category}
        total={group.policies.length}
        restrictionCount={group.restrictionCount}
        open={isCategoryOpen(group.category)}
        onToggle={() => onToggleCategory(group.category)}
        isFirst={index === 0}
      >
        {group.policies.map((policy) => (
          <PolicyCard
            key={policy.id}
            policy={policy}
            partnerId={partnerId}
            expanded={expanded.includes(policy.id)}
            hasAccessToManageSupportPolicies={hasAccessToManageSupportPolicies}
            onExpandClick={() => handleExpandClick(policy.id)}
            onMenuOpen={(e) => handleMenuOpen(e, policy.id)}
            evaluationServiceLevel={evaluationServiceLevel}
            currentTime={stableCurrentTime}
            policyEvaluationContext={policyEvaluationContext}
            isPreLits={isPreLits}
            getEscalationCutoffHoursForUrgency={getEscalationCutoffHoursForUrgency}
            isFocused={focusPolicyId === policy.id}
            onFocusHandled={onFocusPolicyHandled}
          />
        ))}
      </PolicyCategoryGroupSection>
    ));
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }} data-testid="support-policy-list">
      {content}
    </Box>
  );
};
